import React from 'react';
import {
    Snackbar,
    Alert,
    AlertTitle,
    Box,
    IconButton,
    Typography,
    Slide,
    SlideProps
} from '@mui/material';
import {
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
    open: boolean;
    message: string;
    title?: string;
    severity?: ToastSeverity;
    duration?: number;
    onClose: () => void;
}

function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction="left" />;
}

const Toast: React.FC<ToastProps> = ({
    open,
    message,
    title,
    severity = 'success',
    duration = 5000,
    onClose
}) => {
    const { theme } = useTheme();

    const getIcon = () => {
        const iconSize = 28;
        switch (severity) {
            case 'success':
                return <SuccessIcon sx={{ fontSize: iconSize }} />;
            case 'error':
                return <ErrorIcon sx={{ fontSize: iconSize }} />;
            case 'warning':
                return <WarningIcon sx={{ fontSize: iconSize }} />;
            case 'info':
                return <InfoIcon sx={{ fontSize: iconSize }} />;
            default:
                return <SuccessIcon sx={{ fontSize: iconSize }} />;
        }
    };

    const getDefaultTitle = () => {
        switch (severity) {
            case 'success':
                return 'Success';
            case 'error':
                return 'Error';
            case 'warning':
                return 'Warning';
            case 'info':
                return 'Information';
            default:
                return 'Success';
        }
    };

    const getColors = () => {
        switch (severity) {
            case 'success':
                return {
                    bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    iconBg: 'rgba(255, 255, 255, 0.2)',
                    border: 'rgba(255, 255, 255, 0.3)'
                };
            case 'error':
                return {
                    bg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    iconBg: 'rgba(255, 255, 255, 0.2)',
                    border: 'rgba(255, 255, 255, 0.3)'
                };
            case 'warning':
                return {
                    bg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    iconBg: 'rgba(255, 255, 255, 0.2)',
                    border: 'rgba(255, 255, 255, 0.3)'
                };
            case 'info':
                return {
                    bg: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    iconBg: 'rgba(255, 255, 255, 0.2)',
                    border: 'rgba(255, 255, 255, 0.3)'
                };
            default:
                return {
                    bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    iconBg: 'rgba(255, 255, 255, 0.2)',
                    border: 'rgba(255, 255, 255, 0.3)'
                };
        }
    };

    const colors = getColors();

    return (
        <Snackbar
            open={open}
            autoHideDuration={duration}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={SlideTransition}
            sx={{
                mt: 1,
                mr: 1
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    minWidth: 320,
                    maxWidth: 420,
                    p: 2,
                    background: colors.bg,
                    borderRadius: 3,
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: `1px solid ${colors.border}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                {/* Icon Container */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: colors.iconBg,
                        color: 'white',
                        flexShrink: 0,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    {getIcon()}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            color: 'white',
                            lineHeight: 1.3,
                            mb: 0.25,
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                    >
                        {title || getDefaultTitle()}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                        }}
                    >
                        {message}
                    </Typography>
                </Box>

                {/* Close Button */}
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                            color: 'white'
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        </Snackbar>
    );
};

export default Toast;
